// @ts-nocheck
/**
 * club-progressive-discount.js
 *
 * Aplica VISUALMENTE no carrinho um desconto progressivo baseado no
 * subtotal de itens que possuam simultaneamente as tags:
 *   - 'assinatura'
 *   - tag configurável do clube (default: 'clube')
 *
 * O desconto NÃO altera o checkout neste momento. A função
 * `window.ClubDiscount.applyToCheckout` é o ponto de extensão futuro
 * para integração real com a API de descontos da Shopify (ex.: Discount
 * Functions ou geração automática de discount_code).
 *
 * Configuração inicial é injetada via Liquid em `window.__clubDiscount`
 * (ver sections/pricing-plans.liquid).
 */
(function () {
  'use strict';

  // IMPORTANTE: a comparação é case-insensitive — todas as tags do produto
  // são normalizadas para lowercase antes da comparação. Mantenha esta
  // constante em lowercase mesmo que a tag no admin do Shopify esteja
  // capitalizada como "Assinatura".
  var SUBSCRIPTION_TAG = 'assinatura';
  var DEBOUNCE_MS = 250;
  var SUMMARY_ATTR = 'data-club-discount-summary';
  // Re-renders assíncronos do tema (sectionRenderer.renderSection) podem
  // remover nosso bloco DEPOIS do debounce. Disparamos retentativas em
  // intervalos crescentes a cada cart:update para reconciliar.
  var RETRY_DELAYS_MS = [400, 900, 1800];

  // Logs de diagnóstico ativáveis via ?clubdiscount=debug ou
  // localStorage.setItem('clubdiscount:debug', '1')
  var DEBUG = (function () {
    try {
      if (/[?&]clubdiscount=debug/i.test(location.search)) return true;
      if (window.localStorage && localStorage.getItem('clubdiscount:debug') === '1') return true;
    } catch (e) {}
    return false;
  })();
  function log() {
    if (!DEBUG || !window.console) return;
    var args = ['[club-discount]'].concat(Array.prototype.slice.call(arguments));
    (console.info || console.log).apply(console, args);
  }
  function warn() {
    if (!window.console) return;
    var args = ['[club-discount]'].concat(Array.prototype.slice.call(arguments));
    console.warn.apply(console, args);
  }

  var config = window.__clubDiscount || {};
  var clubTag = String(config.clubTag || 'clube').toLowerCase().trim();
  var cartTitle = String(config.cartTitle == null ? '' : config.cartTitle).trim();
  var rules = sanitizeRules(config.rules);
  var currency = config.currency || 'BRL';
  var locale = config.locale || 'pt-BR';

  log('init', { clubTag: clubTag, rules: rules, currency: currency, locale: locale, initialItems: config.initialItems });
  if (rules.length === 0) {
    warn('Nenhuma regra de desconto configurada. Defina "Valor do desconto (em %)" e "Desconto a partir de" nos blocks da section "Planos de Assinatura".');
  }

  // Cache em memória: handle -> array de tags (lowercased)
  var tagMap = Object.create(null);
  // Inflight requests para evitar duplicação ao buscar tags do mesmo handle
  var tagFetchPromises = Object.create(null);
  // Último estado calculado (consumido por reanexar pós-morph e por hooks externos)
  var lastState = null;
  // Guarda contra reentrância em refresh
  var isRefreshing = false;
  var refreshQueued = false;
  var debounceTimer = null;

  prePopulateTagMap(config.initialItems);

  // ---------------------------------------------------------------------------
  // Funções puras / utilitárias
  // ---------------------------------------------------------------------------

  function sanitizeRules(input) {
    if (!Array.isArray(input)) return [];
    var out = [];
    for (var i = 0; i < input.length; i++) {
      var r = input[i] || {};
      var start = Number(r.start);
      var percentage = Number(r.percentage);
      if (isFinite(start) && isFinite(percentage) && percentage > 0) {
        out.push({ start: start, percentage: percentage });
      }
    }
    return out;
  }

  function prePopulateTagMap(items) {
    if (!Array.isArray(items)) return;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it && it.handle && Array.isArray(it.tags)) {
        tagMap[it.handle] = normalizeTags(it.tags);
      }
    }
  }

  function normalizeTags(tags) {
    var out = [];
    for (var i = 0; i < tags.length; i++) {
      var t = String(tags[i] || '').toLowerCase().trim();
      if (t) out.push(t);
    }
    return out;
  }

  /**
   * @param {string[]} productTags
   * @param {string} requiredClubTag
   * @returns {boolean}
   */
  function hasRequiredTags(productTags, requiredClubTag) {
    if (!Array.isArray(productTags) || productTags.length === 0) return false;
    var subTagLower = SUBSCRIPTION_TAG.toLowerCase();
    var clubTagLower = String(requiredClubTag || '').toLowerCase();
    var hasSubscription = false;
    var hasClub = false;
    for (var i = 0; i < productTags.length; i++) {
      // productTags já vem normalizada (lowercase + trimmed), mas defendemos
      // contra entradas externas
      var t = String(productTags[i] || '').toLowerCase().trim();
      if (t === subTagLower) hasSubscription = true;
      if (t === clubTagLower) hasClub = true;
      if (hasSubscription && hasClub) return true;
    }
    return false;
  }

  /**
   * Soma final_line_price (centavos) dos itens elegíveis.
   * @param {Array} cartItems   itens vindos de /cart.js
   * @param {string} requiredClubTag
   * @param {Object} tags       map handle -> string[]
   * @returns {number} subtotal em centavos
   */
  function calculateEligibleSubtotal(cartItems, requiredClubTag, tags) {
    if (!Array.isArray(cartItems)) return 0;
    var subtotal = 0;
    for (var i = 0; i < cartItems.length; i++) {
      var item = cartItems[i];
      var handle = item && item.handle;
      var itemTags = (handle && tags[handle]) || [];
      if (hasRequiredTags(itemTags, requiredClubTag)) {
        // final_line_price respeita descontos de linha e quantidade
        subtotal += Number(item.final_line_price) || 0;
      }
    }
    return subtotal;
  }

  /**
   * Seleciona o maior desconto cuja condição (subtotal >= start) seja satisfeita.
   * @param {number} subtotalCents
   * @param {Array<{start:number, percentage:number}>} discountRules
   * @returns {{percentage:number, start:number}|null}
   */
  function getApplicableDiscount(subtotalCents, discountRules) {
    if (!Array.isArray(discountRules) || discountRules.length === 0) return null;
    var subtotalReais = subtotalCents / 100;
    var best = null;
    for (var i = 0; i < discountRules.length; i++) {
      var rule = discountRules[i];
      if (subtotalReais >= rule.start) {
        if (!best || rule.start > best.start) {
          best = rule;
        }
      }
    }
    return best;
  }

  /**
   * @param {number} subtotalCents
   * @param {number} percentage  ex.: 20 para 20%
   * @returns {{subtotalCents:number, discountCents:number, discountedCents:number, percentage:number}}
   */
  function calculateDiscountValues(subtotalCents, percentage) {
    var pct = Number(percentage) || 0;
    var discountCents = Math.round((subtotalCents * pct) / 100);
    var discountedCents = subtotalCents - discountCents;
    return {
      subtotalCents: subtotalCents,
      discountCents: discountCents,
      discountedCents: discountedCents,
      percentage: pct
    };
  }

  /**
   * Calcula dados para a barra de progresso visual de descontos.
   *
   * - Ordena as regras por `percentage` ASC (do menor para o maior desconto).
   * - Cada tier `i` (0..N-1) recebe posição `((i + 1) / N) * 100%` na barra,
   *   de forma que o maior desconto fica em 100%.
   * - O fill SNAPS na posição do tier atualmente aplicado, usando exatamente
   *   `getApplicableDiscount` para garantir sincronia perfeita com o desconto
   *   exibido no texto do summary. Assim, dots e fill nunca ficam fora de
   *   sincronia, mesmo se o usuário configurar tiers com o mesmo `start`
   *   ou em ordens não-monotônicas.
   * - Antes do primeiro tier, o fill cresce suavemente de 0 até o primeiro
   *   dot, proporcional a `subtotal / tiers[0].start`, para o usuário ver
   *   o progresso inicial.
   *
   * @param {number} subtotalCents
   * @param {Array<{start:number, percentage:number}>} discountRules
   * @returns {{fillPercent:number, markers:Array<{position:number, percentage:number, start:number, reached:boolean}>}}
   */
  function computeBarProgress(subtotalCents, discountRules) {
    if (!Array.isArray(discountRules) || discountRules.length === 0) {
      return { fillPercent: 0, markers: [] };
    }
    var subtotalReais = (Number(subtotalCents) || 0) / 100;
    var sorted = discountRules.slice().sort(function (a, b) {
      return a.percentage - b.percentage;
    });
    var n = sorted.length;
    var markers = [];
    for (var i = 0; i < n; i++) {
      var position = ((i + 1) / n) * 100;
      markers.push({
        position: position,
        percentage: sorted[i].percentage,
        start: sorted[i].start,
        reached: subtotalReais >= Number(sorted[i].start)
      });
    }

    // Usamos a MESMA função que define o desconto exibido no texto, para
    // que o fill e o "X% Desconto Clube Orfeu" estejam sempre em sincronia.
    var applied = getApplicableDiscount(subtotalCents, discountRules);
    var fillPercent = 0;

    if (!applied) {
      // Nenhum tier conquistado: cresce linearmente de 0 até o 1º dot.
      var firstStart = Number(sorted[0].start) || 0;
      if (subtotalReais > 0 && firstStart > 0) {
        fillPercent = (subtotalReais / firstStart) * markers[0].position;
      }
    } else {
      // Encontra o índice do tier aplicado dentro do array já ordenado por
      // percentage (= ordem visual dos dots na barra).
      var snapIdx = 0;
      for (var k = 0; k < n; k++) {
        if (
          Number(sorted[k].percentage) === Number(applied.percentage) &&
          Number(sorted[k].start) === Number(applied.start)
        ) {
          snapIdx = k;
          break;
        }
      }
      fillPercent = markers[snapIdx].position;
    }

    if (fillPercent < 0) fillPercent = 0;
    if (fillPercent > 100) fillPercent = 100;
    return { fillPercent: fillPercent, markers: markers };
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents, curr, loc) {
    var value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(loc || locale, {
        style: 'currency',
        currency: curr || currency
      }).format(value);
    } catch (e) {
      return 'R$ ' + value.toFixed(2).replace('.', ',');
    }
  }

  // ---------------------------------------------------------------------------
  // Lookup de tags (cache + fetch sob demanda)
  // ---------------------------------------------------------------------------

  function getProductTags(handle) {
    if (!handle) return Promise.resolve([]);
    if (tagMap[handle]) return Promise.resolve(tagMap[handle]);
    if (tagFetchPromises[handle]) return tagFetchPromises[handle];

    var p = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to fetch product ' + handle);
        return res.json();
      })
      .then(function (product) {
        var tags = normalizeTags(product && product.tags ? product.tags : []);
        tagMap[handle] = tags;
        return tags;
      })
      .catch(function () {
        // Em caso de falha, cacheia vazio para evitar refetch em loop
        tagMap[handle] = [];
        return [];
      })
      .then(function (tags) {
        delete tagFetchPromises[handle];
        return tags;
      });

    tagFetchPromises[handle] = p;
    return p;
  }

  // ---------------------------------------------------------------------------
  // Renderização do resumo no cart drawer / cart page
  // ---------------------------------------------------------------------------

  function findSummaryHosts() {
    // Pode existir mais de um (cart drawer + cart page no mesmo DOM em alguns layouts)
    return document.querySelectorAll('.cart__summary-totals');
  }

  function buildBarMarkup(barData) {
    // Barra de progresso (marcadores das metas de cada desconto, similar à
    // barra de frete grátis) DESABILITADA temporariamente a pedido.
    // Para reativar, basta remover este `return ''` e restaurar a lógica abaixo.
    return '';

    // eslint-disable-next-line no-unreachable
    if (!barData || !Array.isArray(barData.markers) || barData.markers.length === 0) {
      return '';
    }
    var markersHtml = '';
    for (var i = 0; i < barData.markers.length; i++) {
      var m = barData.markers[i];
      var reachedClass = m.reached ? ' club-discount-bar__marker--reached' : '';
      var pos = Math.max(0, Math.min(100, Number(m.position) || 0));
      markersHtml +=
        '<span class="club-discount-bar__marker' + reachedClass + '" style="left:' + pos.toFixed(2) + '%">' +
          '<span class="club-discount-bar__marker-label">' + m.percentage + '%</span>' +
          '<span class="club-discount-bar__marker-dot"></span>' +
        '</span>';
    }
    var fill = Math.max(0, Math.min(100, Number(barData.fillPercent) || 0));
    return (
      '<div class="club-discount-bar" role="img" aria-label="Progresso de descontos">' +
        '<div class="club-discount-bar__markers">' + markersHtml + '</div>' +
        '<div class="club-discount-bar__track">' +
          '<div class="club-discount-bar__fill" style="width:' + fill.toFixed(2) + '%"></div>' +
        '</div>' +
      '</div>'
    );
  }

  function buildSummaryNode(values, curr, loc, barData) {
    var node = document.createElement('div');
    node.className = 'club-discount-summary';
    node.setAttribute(SUMMARY_ATTR, '');
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');

    var titleHtml = cartTitle
      ? '<h3 class="club-discount-title">' + escapeHtml(cartTitle) + '</h3>'
      : '';

    node.innerHTML =
      titleHtml +
      buildBarMarkup(barData) +
      '<span class="club-discount-summary__row club-discount-summary__row--discount">' +
        '<span class="club-discount-summary__label club-discount-summary__label--discount">Você está ganhando ' + values.percentage + '% de desconto</span>'
      '</span>'
    return node;
  }

  function removeSummaryFromAllHosts() {
    var existing = document.querySelectorAll('[' + SUMMARY_ATTR + ']');
    for (var i = 0; i < existing.length; i++) {
      existing[i].parentNode && existing[i].parentNode.removeChild(existing[i]);
    }
  }

  /**
   * @param {Object} data
   * @param {{subtotalCents:number, discountCents:number, discountedCents:number, percentage:number}} data.values
   */
  function renderDiscountSummary(data) {
    if (!data || !data.values || data.values.subtotalCents <= 0 || data.values.percentage <= 0) {
      log('render: nada a exibir (sem itens elegíveis ou sem regra aplicável)');
      removeSummaryFromAllHosts();
      return;
    }
    var hosts = findSummaryHosts();
    if (hosts.length === 0) {
      warn('render: .cart__summary-totals não encontrado no DOM. O bloco será injetado quando o cart drawer renderizar (próximo cart:update ou abrir do drawer).');
      return;
    }
    log('render: injetando em', hosts.length, 'host(s)');

    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      var existing = host.querySelector('[' + SUMMARY_ATTR + ']');
      var node = buildSummaryNode(data.values, data.currency, data.locale, data.bar);

      if (existing) {
        existing.replaceWith(node);
      } else {
        // Insere como PRIMEIRO filho de .cart__summary-totals para ficar
        // acima de qualquer outro item do summary — incluindo desconto do
        // PIX e demais injeções de apps externos.
        if (host.firstChild) {
          host.insertBefore(node, host.firstChild);
        } else {
          host.appendChild(node);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Fluxo principal: refreshAndRender
  // ---------------------------------------------------------------------------

  function refreshAndRender() {
    if (rules.length === 0) {
      // Sem regras configuradas, garante que nada visualmente fique pendurado.
      removeSummaryFromAllHosts();
      return Promise.resolve();
    }

    if (isRefreshing) {
      refreshQueued = true;
      return Promise.resolve();
    }
    isRefreshing = true;

    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to fetch /cart.js');
        return res.json();
      })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        // Garante tags para todos os handles do cart antes do cálculo
        var promises = [];
        for (var i = 0; i < items.length; i++) {
          var h = items[i] && items[i].handle;
          if (h && !tagMap[h]) promises.push(getProductTags(h));
        }
        return Promise.all(promises).then(function () { return items; });
      })
      .then(function (items) {
        var subtotalCents = calculateEligibleSubtotal(items, clubTag, tagMap);
        var rule = getApplicableDiscount(subtotalCents, rules);
        var percentage = rule ? rule.percentage : 0;
        var values = calculateDiscountValues(subtotalCents, percentage);
        var eligibleItemCount = countEligible(items);
        var bar = computeBarProgress(subtotalCents, rules);

        lastState = {
          values: values,
          rule: rule,
          bar: bar,
          currency: currency,
          locale: locale,
          eligibleItemCount: eligibleItemCount
        };

        if (DEBUG) {
          var debugItems = items.map(function (it) {
            return {
              handle: it.handle,
              tags: tagMap[it.handle] || [],
              eligible: hasRequiredTags(tagMap[it.handle] || [], clubTag),
              final_line_price: it.final_line_price
            };
          });
          log('refresh result', {
            cartItems: debugItems,
            subtotalCents: subtotalCents,
            ruleApplied: rule,
            values: values,
            eligibleItemCount: eligibleItemCount,
            hostsFound: findSummaryHosts().length
          });
        }

        renderDiscountSummary(lastState);
      })
      .catch(function (err) {
        // Falha não-fatal: não polui o UI do carrinho
        if (window.console && console.warn) {
          console.warn('[club-progressive-discount]', err);
        }
      })
      .then(function () {
        isRefreshing = false;
        if (refreshQueued) {
          refreshQueued = false;
          // Próximo tick para evitar stack profundo
          setTimeout(refreshAndRender, 0);
        }
      });
  }

  function countEligible(items) {
    var c = 0;
    for (var i = 0; i < items.length; i++) {
      var h = items[i] && items[i].handle;
      var t = (h && tagMap[h]) || [];
      if (hasRequiredTags(t, clubTag)) c++;
    }
    return c;
  }

  function debouncedRefresh() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(refreshAndRender, DEBOUNCE_MS);
  }

  /**
   * Após um cart:update, o tema pode chamar sectionRenderer.renderSection
   * de forma ASSÍNCRONA, removendo nosso bloco depois do nosso debounce.
   * Para sobreviver a isso, agendamos retentativas em intervalos crescentes:
   * cada uma só re-renderiza se nosso bloco sumiu do DOM.
   */
  function scheduleRetries() {
    for (var i = 0; i < RETRY_DELAYS_MS.length; i++) {
      (function (delay) {
        setTimeout(function () {
          if (!lastState || !lastState.values || lastState.values.subtotalCents <= 0) {
            // Estado inválido: dispara um refresh real para reconciliar
            refreshAndRender();
            return;
          }
          var hostsExist = findSummaryHosts().length > 0;
          var summaryExists = !!document.querySelector('[' + SUMMARY_ATTR + ']');
          if (hostsExist && !summaryExists) {
            log('retry@' + delay + 'ms: re-injetando bloco (DOM foi re-renderizado)');
            renderDiscountSummary(lastState);
          }
        }, delay);
      })(RETRY_DELAYS_MS[i]);
    }
  }

  function onCartChanged(eventName) {
    return function () {
      log('event recebido:', eventName);
      debouncedRefresh();
      scheduleRetries();
    };
  }

  // ---------------------------------------------------------------------------
  // Reanexar após morph: o tema re-renderiza .cart__summary-totals em updates,
  // o que pode remover o nosso bloco. Observamos o ancestral para reinjetar
  // imediatamente usando o último estado calculado (evita flicker), e em
  // seguida disparamos um refresh real para reconciliar valores.
  // ---------------------------------------------------------------------------

  var domObserver = null;
  function ensureMutationObserver() {
    if (domObserver) return;
    if (typeof MutationObserver === 'undefined') return;

    domObserver = new MutationObserver(function (mutations) {
      var summaryMissing = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) {
          // Se temos lastState válido mas não há sumário no DOM, reanexa
          if (lastState && lastState.values && lastState.values.subtotalCents > 0) {
            if (!document.querySelector('[' + SUMMARY_ATTR + ']') && findSummaryHosts().length > 0) {
              summaryMissing = true;
              break;
            }
          }
        }
      }
      if (summaryMissing) {
        renderDiscountSummary(lastState);
        // Reconcilia em seguida (cart.js pode ter mudado)
        debouncedRefresh();
      }
    });

    domObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // ---------------------------------------------------------------------------
  // Bootstrap + listeners
  // ---------------------------------------------------------------------------

  function init() {
    ensureMutationObserver();
    refreshAndRender();
    bindCartDrawerOpen();
  }

  // Garante que o bloco apareça quando o usuário abre o cart drawer
  // (mesmo sem cart:update prévio nesta sessão).
  function bindCartDrawerOpen() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    var dialog = drawer.querySelector('dialog.cart-drawer__dialog');
    if (!dialog) return;
    // <dialog> não emite "open" nativamente, mas o tema usa show()/showModal().
    // Observamos abertura via attribute change (open) e via toggle event (futuro).
    if (typeof MutationObserver !== 'undefined') {
      var dlgObs = new MutationObserver(function () {
        if (dialog.hasAttribute('open')) {
          log('cart drawer aberto: forçando re-render');
          if (lastState && lastState.values && lastState.values.subtotalCents > 0) {
            renderDiscountSummary(lastState);
          }
          refreshAndRender();
        }
      });
      dlgObs.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
  }

  document.addEventListener('cart:update', onCartChanged('cart:update'));
  document.addEventListener('cart:add', onCartChanged('cart:add'));
  // Fallbacks para temas/snippets que disparem outros nomes de evento
  document.addEventListener('cart:refresh', onCartChanged('cart:refresh'));
  document.addEventListener('cart:change', onCartChanged('cart:change'));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ---------------------------------------------------------------------------
  // Hook público para futura integração com checkout
  // ---------------------------------------------------------------------------

  window.ClubDiscount = {
    recalculate: refreshAndRender,
    getCurrentState: function () {
      // Retorna cópia rasa para evitar mutação externa do cache interno
      if (!lastState) return null;
      return {
        values: Object.assign({}, lastState.values),
        rule: lastState.rule ? Object.assign({}, lastState.rule) : null,
        bar: lastState.bar ? {
          fillPercent: lastState.bar.fillPercent,
          markers: (lastState.bar.markers || []).map(function (m) {
            return Object.assign({}, m);
          })
        } : null,
        currency: lastState.currency,
        locale: lastState.locale,
        eligibleItemCount: lastState.eligibleItemCount
      };
    },
    /**
     * Ponto de extensão para aplicar o desconto REAL no checkout.
     * Hoje o desconto é apenas visual. Para integração futura, este método
     * deve, por exemplo, gerar/aplicar um discount_code via Storefront API,
     * acionar uma Discount Function, ou chamar um endpoint do app de descontos.
     *
     * @param {Object} state  resultado de getCurrentState()
     * @returns {Promise<void>}
     */
    applyToCheckout: function (/* state */) {
      // TODO: integrar com Shopify Discounts API / Discount Functions.
      return Promise.resolve();
    },
    // Expostos para testes / debug; manter superfície estável
    _internals: {
      hasRequiredTags: hasRequiredTags,
      calculateEligibleSubtotal: calculateEligibleSubtotal,
      getApplicableDiscount: getApplicableDiscount,
      calculateDiscountValues: calculateDiscountValues,
      computeBarProgress: computeBarProgress,
      formatMoney: formatMoney
    }
  };
})();
